import { useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clipboard,
  FileText,
  Info,
  LoaderCircle,
  Paperclip,
  Send,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button, ButtonLink, Card, FieldError, Input, Label, PageHeader, Select, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_TOTAL_SIZE,
  MAX_ATTACHMENT_SIZE,
  formatFileSize,
  isAllowedAttachment,
  isImageAttachment,
} from "../lib/attachments";
import { useRouter } from "../lib/router";
import type { TicketCategory } from "../types/api";

type FormState = {
  companyId: string;
  categoryId: string;
  priority: string;
  phone: string;
  cc: string;
  description: string;
};

const initialForm: FormState = {
  companyId: "",
  categoryId: "",
  priority: "Baixa",
  phone: "",
  cc: "",
  description: "",
};

function LocalFilePreview({ file }: { file: File }) {
  const isImage = isImageAttachment(file.name);
  const source = useMemo(
    () => (isImage ? URL.createObjectURL(file) : ""),
    [file, isImage],
  );

  if (!source) {
    return <span><FileText /></span>;
  }

  return (
    <img
      src={source}
      alt={`Prévia de ${file.name}`}
      onLoad={() => URL.revokeObjectURL(source)}
    />
  );
}

export function NewTicketPage() {
  const { session } = useAuth();
  const { navigate } = useRouter();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>({
    ...initialForm,
    phone: session?.telefone ?? "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const isMira = session?.codigoEmpresa === 52;

  const categories = useQuery({
    queryKey: ["ticket-categories"],
    queryFn: api.getCategories,
  });
  const companies = useQuery({
    queryKey: ["companies", "new-ticket"],
    queryFn: api.getCompanies,
    enabled: isMira,
  });

  const groupedCategories = useMemo(() => {
    return (categories.data ?? []).reduce<Record<string, TicketCategory[]>>(
      (groups, item) => {
        const group = item.nome_categoria || "Outros";
        groups[group] = [...(groups[group] ?? []), item];
        return groups;
      },
      {},
    );
  }, [categories.data]);
  const selectedCompany = companies.data?.find(
    (company) => String(company.id) === form.companyId,
  );

  const createTicket = useMutation({
    mutationFn: async () => {
      const ticketId = await api.createTicket({
        codigoEmpresa: isMira ? Number(form.companyId) : session!.codigoEmpresa,
        prioridade: form.priority,
        descricao: form.description.trim(),
        telefone: form.phone.trim(),
        subCategoriaId: Number(form.categoryId),
        status: "NOVO",
        cc: form.cc.trim(),
      });

      if (files.length) {
        try {
          await api.uploadOpeningAttachments(ticketId, files);
        } catch {
          toast.warning(
            `O chamado #${ticketId} foi aberto, mas os arquivos não puderam ser anexados.`,
          );
        }
      }
      return ticketId;
    },
    onSuccess: (ticketId) => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success(`Chamado #${ticketId} aberto com sucesso.`);
      navigate(`/chamados/${ticketId}`);
    },
    onError: () => {
      toast.error("Não foi possível abrir o chamado. Revise os dados e tente novamente.");
    },
  });

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function validate() {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    if (isMira && !form.companyId) nextErrors.companyId = "Selecione a empresa.";
    if (!form.categoryId) nextErrors.categoryId = "Selecione uma categoria.";
    if (!form.phone.trim()) nextErrors.phone = "Informe um telefone para contato.";
    if (form.description.trim().length < 15) {
      nextErrors.description = "Descreva o problema com pelo menos 15 caracteres.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (validate()) createTicket.mutate();
  }

  function addFiles(nextFiles: File[]) {
    const merged = [...files];

    for (const file of nextFiles) {
      if (!isAllowedAttachment(file.name)) {
        toast.error(`O formato de "${file.name}" não é permitido.`);
        continue;
      }
      if (file.size === 0 || file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`"${file.name}" deve ter no máximo 10 MB.`);
        continue;
      }
      const duplicated = merged.some(
        (current) =>
          current.name === file.name &&
          current.size === file.size &&
          current.lastModified === file.lastModified,
      );
      if (!duplicated) merged.push(file);
    }

    if (merged.length > MAX_ATTACHMENTS) {
      toast.error(`Você pode inserir no máximo ${MAX_ATTACHMENTS} arquivos.`);
      return;
    }
    if (
      merged.reduce((total, file) => total + file.size, 0) >
      MAX_ATTACHMENTS_TOTAL_SIZE
    ) {
      toast.error("O total dos arquivos não pode ultrapassar 25 MB.");
      return;
    }

    setFiles(merged);
  }

  return (
    <div className="page-stack max-w-[1120px]">
      <PageHeader
        eyebrow="Nova solicitação"
        title="Abrir chamado"
        description="Conte o que está acontecendo. Os campos abaixo ajudam a direcionar sua solicitação."
        actions={
          <ButtonLink to="/chamados" variant="secondary">
            <ArrowLeft className="size-4" />
            Voltar
          </ButtonLink>
        }
      />

      <div className="stepper">
        <div className="active"><span>1</span><div><strong>Informações</strong><small>Detalhes da solicitação</small></div></div>
        <ChevronRight />
        <div><span>2</span><div><strong>Confirmação</strong><small>Chamado protocolado</small></div></div>
      </div>

      <form onSubmit={submit} className="new-ticket-layout">
        <div className="space-y-5">
          <Card className="form-section">
            <div className="form-section-heading">
              <span>01</span>
              <div>
                <h2>Classificação</h2>
                <p>Escolha o tipo e a urgência da solicitação.</p>
              </div>
            </div>
            <div className="form-grid">
              {isMira && (
                <div className="md:col-span-2">
                  <Label htmlFor="company">Empresa do chamado *</Label>
                  <Select
                    id="company"
                    value={form.companyId}
                    onChange={(event) => setField("companyId", event.target.value)}
                    disabled={companies.isLoading}
                    aria-invalid={Boolean(errors.companyId)}
                  >
                    <option value="">
                      {companies.isLoading ? "Carregando empresas..." : "Selecione uma empresa"}
                    </option>
                    {companies.data?.map((company) => (
                      <option key={company.id} value={company.id}>{company.empresa}</option>
                    ))}
                  </Select>
                  <FieldError>{errors.companyId}</FieldError>
                </div>
              )}
              <div className="md:col-span-2">
                <Label htmlFor="category">Categoria do chamado *</Label>
                <Select
                  id="category"
                  value={form.categoryId}
                  onChange={(event) => setField("categoryId", event.target.value)}
                  disabled={categories.isLoading}
                  aria-invalid={Boolean(errors.categoryId)}
                >
                  <option value="">
                    {categories.isLoading ? "Carregando categorias..." : "Selecione uma categoria"}
                  </option>
                  {Object.entries(groupedCategories).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map((item) => (
                        <option key={item.id_subcategoria} value={item.id_subcategoria}>
                          {item.nome_subcategoria}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
                <FieldError>{errors.categoryId}</FieldError>
              </div>
              <div>
                <Label htmlFor="priority">Prioridade *</Label>
                <Select
                  id="priority"
                  value={form.priority}
                  onChange={(event) => setField("priority", event.target.value)}
                >
                  <option value="Baixa">Baixa — pode aguardar</option>
                  <option value="Média">Média — impacto parcial</option>
                  <option value="Alta">Alta — operação parada</option>
                  <option value="Altíssima">Altíssima — sistema parado</option>
                </Select>
              </div>
            </div>
            <div className="form-hint">
              <Info />
              Use prioridade alta somente quando o problema impedir a continuidade do trabalho.
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>02</span>
              <div>
                <h2>Descrição do problema</h2>
                <p>Inclua mensagens exibidas, contexto e o resultado esperado.</p>
              </div>
            </div>
            <div>
              <div className="flex items-end justify-between gap-4">
                <Label htmlFor="description">O que está acontecendo? *</Label>
                <span className="text-xs text-slate-400">{form.description.length}/4000</span>
              </div>
              <Textarea
                id="description"
                value={form.description}
                onChange={(event) =>
                  setField("description", event.target.value.slice(0, 4000))
                }
                onPaste={(event) => {
                  const pastedFiles = Array.from(event.clipboardData.files);
                  if (pastedFiles.length) {
                    event.preventDefault();
                    addFiles(pastedFiles);
                    toast.success("Arquivo adicionado aos anexos.");
                  }
                }}
                placeholder={"Exemplo: ao emitir a nota fiscal, aparece a mensagem...\n\nVocê também pode colar um print aqui usando Ctrl+V."}
                className="min-h-56"
                aria-invalid={Boolean(errors.description)}
              />
              <FieldError>{errors.description}</FieldError>
            </div>

            <div className="description-images">
              <div className="description-images-heading">
                <div>
                  <Label>Anexos da descrição (opcional)</Label>
                  <p>
                    Cole um print com Ctrl+V, arraste ou selecione até{" "}
                    {MAX_ATTACHMENTS} arquivos (imagem, PDF, Word, Excel...).
                  </p>
                </div>
                <span>{files.length}/{MAX_ATTACHMENTS}</span>
              </div>
              <button
                className="inline-image-dropzone"
                type="button"
                onClick={() => fileInput.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  addFiles(Array.from(event.dataTransfer.files));
                }}
              >
                <span><Paperclip /></span>
                <div>
                  <strong>Anexar arquivo, imagem ou captura de tela</strong>
                  <small><Clipboard /> Você também pode usar Ctrl+V no campo acima</small>
                </div>
              </button>
              <input
                ref={fileInput}
                className="hidden"
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={(event) => {
                  addFiles(Array.from(event.target.files ?? []));
                  event.target.value = "";
                }}
              />
              {files.length ? (
                <div className="description-image-grid">
                  {files.map((file) => (
                    <figure key={`${file.name}-${file.size}-${file.lastModified}`}>
                      <LocalFilePreview file={file} />
                      <figcaption>
                        <span>
                          <strong>{file.name}</strong>
                          <small>{formatFileSize(file.size)}</small>
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFiles((current) =>
                              current.filter((item) => item !== file),
                            )
                          }
                          aria-label={`Remover ${file.name}`}
                        >
                          <Trash2 />
                        </button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : null}
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>03</span>
              <div>
                <h2>Contato</h2>
                <p>Como podemos falar com você durante o atendimento?</p>
              </div>
            </div>
            <div className="form-grid">
              <div>
                <Label htmlFor="phone">Telefone para contato *</Label>
                <Input
                  id="phone"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(event) => setField("phone", event.target.value)}
                  placeholder="(00) 00000-0000"
                  aria-invalid={Boolean(errors.phone)}
                />
                <FieldError>{errors.phone}</FieldError>
              </div>
              <div>
                <Label htmlFor="cc">E-mail em cópia</Label>
                <Input
                  id="cc"
                  type="email"
                  value={form.cc}
                  onChange={(event) => setField("cc", event.target.value)}
                  placeholder="nome@empresa.com.br"
                />
              </div>
            </div>
          </Card>
        </div>

        <aside className="submit-summary">
          <Card>
            <h3>Antes de enviar</h3>
            <ul>
              <li><Check /> Escolha a categoria mais próxima do problema.</li>
              <li><Check /> Informe o texto exato de mensagens de erro.</li>
              <li><Check /> Anexe uma imagem ou documento se ajudar na análise.</li>
            </ul>
            <div className="summary-company">
              <span>Empresa</span>
              <strong>{isMira ? (selectedCompany?.empresa ?? "Selecione a empresa") : session?.empresa}</strong>
            </div>
            <Button type="submit" size="lg" loading={createTicket.isPending} className="w-full">
              {createTicket.isPending ? "Enviando..." : "Abrir chamado"}
              {!createTicket.isPending && <Send />}
            </Button>
            {createTicket.isPending && (
              <p className="submit-progress"><LoaderCircle /> Registrando sua solicitação...</p>
            )}
            <p className="secure-note"><ShieldCheck /> Seus dados serão enviados em uma conexão protegida.</p>
          </Card>
        </aside>
      </form>
    </div>
  );
}
